import { useState } from "react";
import axios from "axios";
import { Mic } from "lucide-react";
import { Reveal, SectionTag, StatusPill } from "./Reveal";
import AriaSessionModal from "./AriaSessionModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AriaLive() {
  const [ariaState, setAriaState] = useState("idle"); // idle | unlock | connecting | session
  const [ariaEmail, setAriaEmail] = useState("");
  const [ariaHoney, setAriaHoney] = useState("");
  const [ariaError, setAriaError] = useState("");
  const [ariaSession, setAriaSession] = useState(null);

  const startAria = async (e) => {
    e.preventDefault();
    setAriaError("");
    setAriaState("connecting");
    try {
      const { data } = await axios.post(`${API}/aria/unlock`, { email: ariaEmail, website: ariaHoney });
      setAriaSession(data);
      setAriaState("session");
    } catch (err) {
      setAriaError(
        err?.response?.data?.detail || "Something went wrong. Please try again in a few minutes."
      );
      setAriaState("unlock");
    }
  };

  return (
    <section id="aria-live" data-testid="aria-live-section" className="relative py-16 lg:py-36">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 lg:grid-cols-2 lg:gap-16 lg:px-10">
        <Reveal>
          <SectionTag>Early Preview</SectionTag>
          <h2 className="font-display mt-5 text-3xl font-light tracking-tighter text-white sm:text-4xl lg:text-5xl">
            Meet Aria — Your <span className="text-cyan-300">Live AI Companion</span>
          </h2>
          <p className="mt-6 max-w-lg text-base leading-relaxed text-[#C5CBD5]">
            Experience real-time conversations with Aria, our photorealistic AI video companion.
            Talk naturally, interact through voice, and discover the future of AI presence.
          </p>
          <p className="mt-4 max-w-lg text-sm leading-relaxed text-white/55">
            Live sessions are currently limited to approved testers while Level 1 remains in
            development.
          </p>
          <div className="mt-5 flex flex-wrap gap-2.5">
            <StatusPill tone="amber">MVP currently in development</StatusPill>
            <StatusPill tone="cyan">Early Preview · AI Companion</StatusPill>
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-5">
            <button
              data-testid="aria-live-cta"
              onClick={() => {
                setAriaState("unlock");
                setAriaError("");
              }}
              className="group inline-flex items-center gap-2 rounded-md bg-cyan-300 px-7 py-4 text-sm font-bold uppercase tracking-[0.1em] text-black transition-transform duration-300 hover:-translate-y-0.5 hover:bg-cyan-200 active:scale-[0.98]"
            >
              <Mic size={16} />
              Talk to Aria
            </button>
            <a
              href="/register"
              data-testid="aria-live-register-link"
              className="text-sm font-medium text-cyan-200/70 transition-colors duration-300 hover:text-cyan-100"
            >
              Not a tester yet? Register for Early Access
            </a>
          </div>

          {(ariaState === "unlock" || ariaState === "connecting") && (
            <form
              data-testid="aria-unlock-form"
              onSubmit={startAria}
              className="mt-8 max-w-lg rounded-xl border border-white/10 bg-white/[0.03] p-5"
            >
              <label htmlFor="aria-unlock-email" className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.2em] text-white/45">
                Approved tester email
              </label>
              <div className="flex flex-col gap-3 sm:flex-row">
                <input
                  id="aria-unlock-email"
                  type="email"
                  required
                  autoComplete="email"
                  data-testid="aria-unlock-email"
                  value={ariaEmail}
                  onChange={(e) => setAriaEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full flex-1 rounded-md border border-white/12 bg-black/40 px-4 py-3 text-sm text-white placeholder-white/30 outline-none transition-colors duration-300 focus:border-cyan-300/60"
                />
                <input
                  type="text"
                  name="website"
                  data-testid="aria-unlock-honeypot"
                  value={ariaHoney}
                  onChange={(e) => setAriaHoney(e.target.value)}
                  className="hidden"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                />
                <button
                  type="submit"
                  data-testid="aria-unlock-submit"
                  disabled={ariaState === "connecting"}
                  className="rounded-md border border-cyan-300/40 px-6 py-3 text-sm font-semibold text-cyan-200 transition-all duration-300 hover:border-cyan-300/70 hover:text-cyan-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {ariaState === "connecting" ? "Connecting…" : "Start Session"}
                </button>
              </div>
              {ariaError && (
                <p data-testid="aria-unlock-error" className="mt-3 text-xs leading-relaxed text-amber-200/90">
                  {ariaError}
                </p>
              )}
            </form>
          )}

          <p className="mt-6 max-w-lg text-xs leading-relaxed text-white/45">
            Aria is an AI companion, not a human. Your browser will ask for microphone permission
            when you start talking — you can also type instead. Preview capacity is limited, so if
            Aria is unavailable, please try again a little later.
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          <div data-testid="aria-live-visual" className="relative mx-auto w-full max-w-sm">
            <div aria-hidden="true" className="absolute -inset-6 rounded-[28px] bg-cyan-400/10 blur-2xl" />
            <div className="relative overflow-hidden rounded-2xl border border-cyan-300/25 shadow-[0_40px_120px_rgba(0,229,255,0.12)]">
              <img
                src="/assets/aria-live-portrait.jpg"
                alt="Aria — live photorealistic AI video companion"
                className="aspect-[0.72] w-full object-cover"
              />
              <div className="absolute left-4 top-4 flex items-center gap-2 rounded-full border border-cyan-300/40 bg-black/70 px-3.5 py-1.5 backdrop-blur-md">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-cyan-300" />
                <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-cyan-200">
                  Early Preview
                </span>
              </div>
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent px-5 pb-4 pt-10">
                <p className="font-display text-sm font-medium text-white">Aria</p>
                <p className="mt-0.5 text-[11px] uppercase tracking-[0.18em] text-white/55">
                  AI Companion · Live video + voice
                </p>
              </div>
            </div>
          </div>
        </Reveal>
      </div>

      {ariaState === "session" && ariaSession && (
        <AriaSessionModal
          roomUrl={ariaSession.room_url}
          token={ariaSession.token}
          onClose={() => {
            setAriaState("idle");
            setAriaSession(null);
          }}
        />
      )}
    </section>
  );
}
